import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';

const MODAL_ANIMATION_MS = 180;

const defaultServices = [
  {
    id: 'fallback-1',
    title: 'Outsourced Accounting',
    shortDescription: 'End-to-end accounting support for growing businesses.',
    details:
      'We manage your daily bookkeeping, reconciliations, financial statements, and monthly reporting so your team can focus on operations and growth.',
  },
  {
    id: 'fallback-2',
    title: 'Tax Advisory',
    shortDescription: 'Strategic tax planning and compliance support.',
    details:
      'Our tax experts help with planning, filings, and compliance to reduce risk and optimize your tax position while staying aligned with regulations.',
  },
  {
    id: 'fallback-3',
    title: 'Company Restructuring',
    shortDescription: 'Financial and operational restructuring guidance.',
    details:
      'We support restructuring initiatives with cashflow reviews, entity optimization, debt strategy input, and practical implementation support.',
  },
  {
    id: 'fallback-4',
    title: 'Start-up Support',
    shortDescription: 'Finance foundations built for early-stage teams.',
    details:
      'From setup and compliance to forecasting and investor-ready reports, we help start-ups build strong financial systems from day one.',
  },
  {
    id: 'fallback-5',
    title: 'Corporate Services',
    shortDescription: 'Reliable back-office and governance support.',
    details:
      'We deliver corporate support services including statutory compliance, process controls, and reporting frameworks for accountable growth.',
  },
  {
    id: 'fallback-6',
    title: 'Financial Planning',
    shortDescription: 'Forward-looking planning and decision support.',
    details:
      'Our planning services include budgeting, scenario modeling, and KPI tracking to improve financial visibility and strategic decision-making.',
  },
];

const normalizeService = service => ({
  ...service,
  shortDescription: service.shortDescription || service.short_description || '',
});

const Footer = ({ onQuoteClick }) => {
  const [services, setServices] = useState(defaultServices);
  const [loadingServices, setLoadingServices] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const closeTimeoutRef = useRef(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoadingServices(true);
        const response = await api.get('/services');
        if (Array.isArray(response.data) && response.data.length > 0) {
          setServices(response.data.map(normalizeService));
        } else {
          setServices(defaultServices);
        }
      } catch (error) {
        console.error('Failed to load services for footer:', error);
        setServices(defaultServices);
      } finally {
        setLoadingServices(false);
      }
    };

    fetchServices();
  }, []);

  useEffect(() => {
    if (selectedService) {
      setIsModalClosing(false);
    }
  }, [selectedService]);

  const closeModal = useCallback(() => {
    if (!selectedService || isModalClosing) {
      return;
    }

    setIsModalClosing(true);
    closeTimeoutRef.current = window.setTimeout(() => {
      setSelectedService(null);
      setIsModalClosing(false);
      closeTimeoutRef.current = null;
    }, MODAL_ANIMATION_MS);
  }, [isModalClosing, selectedService]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!selectedService) {
      return undefined;
    }

    const handleEsc = event => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [closeModal, selectedService]);

  return (
    <>
      <footer className="stratos-footer text-white">
        <div className="stratos-footer-container">
        {/* Call to Action Section */}
        <div className="stratos-footer-cta">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-7">
                <h3 className="stratos-footer-cta-title">Ready to Elevate Your Business?</h3>
                <p className="stratos-footer-cta-text">Join hundreds of successful companies that trust us for their financial strategy.</p>
              </div>
              <div className="col-lg-5 d-flex gap-3 justify-content-lg-end justify-content-center mt-3 mt-lg-0">
                <button
                  onClick={onQuoteClick}
                  className="btn btn-light"
                >
                  Get Started
                </button>
                <Link to="/services" className="btn btn-outline-light">
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </div>

          {/* Main Footer Content */}
          <div className="stratos-footer-main">
            <div className="container">
              <div className="row">
                {/* Brand Section */}
                <div className="col-lg-4 col-md-6 mb-4 mb-lg-0">
                  <div className="footer-brand">
                    <img src="/logow.png" alt="Financial Experts" width="170px" className="mb-3" />
                    <p className="footer-brand-text">At Financial Experts Ltd, we provide comprehensive and strategic financial solutions designed to empower your business growth and success</p>
                  </div>
                </div>

                {/* Services Section */}
                <div className="col-lg-2 col-md-6 mb-4 mb-lg-0">
                  <h6 className="footer-section-title">Our Services</h6>
                  <ul className="footer-links">
                    {loadingServices && <li className="footer-muted">Loading services...</li>}

                    {!loadingServices &&
                      services.map(service => (
                        <li key={service.id || service.title}>
                          <button
                            type="button"
                            className="footer-service-link"
                            onClick={() => setSelectedService(service)}
                          >
                            {service.title}
                          </button>
                        </li>
                      ))}
                  </ul>
                </div>

                {/* Quick Links Section */}
                <div className="col-lg-2 col-md-6 mb-4 mb-lg-0">
                  <h6 className="footer-section-title">Quick Links</h6>
                  <ul className="footer-links">
                    <li><Link to="/">Home</Link></li>
                    <li><Link to="/services">Services</Link></li>
                    <li><Link to="/blog">Blog</Link></li>
                    <li><Link to="/about">About Us</Link></li>
                    <li><a href="#!">Privacy Policy</a></li>
                  </ul>
                </div>

                {/* Contact Section */}
                <div className="col-lg-4 col-md-6 mb-4 mb-lg-0">
                  <h6 className="footer-section-title">Contact Us</h6>
                  <div className="footer-contact-info">
                    <p>
                      <i className="fa fa-map-marker"></i>
                      KN4 AVE- CAR FREE ZONE NEXT TO BK HQ
                    </p>
                    <p>
                      <i className="fa fa-phone"></i>
                      +250788393784
                    </p>
                    <p>
                      <i className="fa fa-envelope"></i>
                      info@icbfinexperts.com
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="stratos-footer-bottom">
            <div className="container">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <p className="mb-0">© {new Date().getFullYear()} Financial Experts Ltd. All rights reserved.</p>
                </div>
                <div className="col-md-6 text-md-end mt-3 mt-md-0">
                  <nav className="footer-social">
                    <a href="#!" className="social-link">
                      <i className="fab fa-linkedin"></i>
                    </a>
                    <a href="#!" className="social-link">
                      <i className="fab fa-instagram"></i>
                    </a>
                    <a href="#!" className="social-link">
                      <i className="fab fa-twitter"></i>
                    </a>
                    <a href="#!" className="social-link">
                      <i className="fab fa-facebook"></i>
                    </a>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>

        {selectedService && (
          <div
            className={`footer-service-modal-overlay${isModalClosing ? ' is-closing' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="footer-service-modal-title"
            onClick={closeModal}
          >
            <div
              className={`footer-service-modal${isModalClosing ? ' is-closing' : ''}`}
              onClick={event => event.stopPropagation()}
            >
              <button
                type="button"
                className="footer-service-modal-close"
                onClick={closeModal}
                aria-label="Close service details"
              >
                ×
              </button>

              <span className="footer-service-modal-eyebrow">Service Overview</span>

              <h5 id="footer-service-modal-title" className="footer-service-modal-title">
                {selectedService.title}
              </h5>

              {selectedService.shortDescription && (
                <p className="footer-service-modal-subtitle">{selectedService.shortDescription}</p>
              )}

              <div className="footer-service-modal-divider" />

              <div className="footer-service-modal-body">
                {(selectedService.details || 'More details about this service are coming soon.')
                  .split(/\n\n+/)
                  .map((para, i) => (
                    <p key={i}>
                      {para.split('\n').map((line, j, arr) => (
                        <React.Fragment key={j}>
                          {line}
                          {j < arr.length - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </p>
                  ))}
              </div>
            </div>
          </div>
        )}
      </footer>
    </>
  );
};

export default Footer;
